import { useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import type { AdsAssistantTemplateRow } from "@/lib/adsAssistantClient";
import { slotIdsFromSlotsSchema } from "@/components/ads-assistant/AdsAssistantSlotFillFields";

/** When the selected template or its slots_schema changes, drop slot_fill keys that no longer exist in the schema. */
export function usePruneSlotFillJsonForTemplate(
  templateId: string,
  templates: AdsAssistantTemplateRow[],
  setSlotFillJsonText: Dispatch<SetStateAction<string>>
) {
  const templateSlotsSchemaKey = useMemo(() => {
    const tpl = templates.find((t) => t.id === templateId);
    const ids = tpl ? slotIdsFromSlotsSchema(tpl.slots_schema) : [];
    return `${templateId}:${ids.slice().sort().join(",")}`;
  }, [templateId, templates]);

  useEffect(() => {
    setSlotFillJsonText((prev) => {
      const tpl = templates.find((t) => t.id === templateId);
      const allowed = new Set(tpl ? slotIdsFromSlotsSchema(tpl.slots_schema) : []);
      const trim = prev.trim();
      if (!trim) return prev;
      try {
        const j = JSON.parse(trim) as unknown;
        if (!j || typeof j !== "object" || Array.isArray(j)) return prev;
        const src = j as Record<string, unknown>;
        const next: Record<string, unknown> = {};
        for (const id of allowed) {
          if (Object.prototype.hasOwnProperty.call(src, id)) next[id] = src[id];
        }
        const out = Object.keys(next).length ? JSON.stringify(next, null, 2) : "";
        return out === trim ? prev : out;
      } catch {
        return prev;
      }
    });
  }, [templateSlotsSchemaKey, templates, templateId, setSlotFillJsonText]);
}
